<template>
  <transition name="slide-chat">
    <v-row no-gutters v-if="!$store.getters.isWaitingInteraction">
      <!-- FULL SCREEN ONLY WEBCAM COLUMN -->
      <v-col
        cols="8"
        class="fill-height"
        style="background-color: black"
        v-if="isOnVideoCall && !$vuetify.breakpoint.smAndDown"
      >
        <call
          :show="isInCall"
          :video="isOnVideoCall"
          @hangupEv="hangup"
          @hideTextAreaEv="switchShowTextInput"
          @phone="onPhone"
          ref="callComponent"
        />
      </v-col>

      <!-- CHAT COLUMN -->
      <v-col
        :cols="isOnVideoCall ? ($vuetify.breakpoint.smAndDown ? 12 : 4) : 12"
      >
        <v-row no-gutters>
          <v-col
            cols="12"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <!-- TOOLBAR -->
            <div class="d-flex align-center">
              <chat-toolbar
                @openConfirmationModal="openCloseDialog"
                @clickCallEv="openWindowCalling"
                @openAccessibilityModal="openAccessibilityModal"
                @clickVideoCallEv="openWindowVideocalling"
                class="width-100"
                :hideEndConv="this.endConv"
                :isBot="this.bot"
              />
            </div>
          </v-col>

          <!-- CLOSE DIALOG -->
          <v-col
            cols="12"
            v-if="showClosingDialog && !showEndConv && !showWebrtcStream.dialog"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <closing-dialog
              :show="showClosingDialog"
              :messageDialogue="$t('closeconf')"
              @confirmClosingEv="shouldClose"
              @cancelClosingEv="hideClosingModal"
            />
          </v-col>

          <!-- CONFIRM CLOSE DIALOG -->
          <v-col
            cols="12"
            v-if="showEndConv && !showWebrtcStream.dialog"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <closing-dialog
              :show="showEndConv"
              :messageDialogue="$t('conversationend')"
              @confirmClosingEv="acceptModalEndConv"
              @cancelClosingEv="cancelModalEndConv"
            ></closing-dialog>
          </v-col>

          <!-- SCREEN SHARE DIALOG -->
          <v-col
            cols="12"
            v-if="showWebrtcStream.dialog && !showClosingDialog && !showEndConv"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <screen-share-dialog
              :show="showWebrtcStream.dialog"
              @yes="screenShareController.shareScreen()"
              @no="screenShareController.cancelShareScreen()"
            />
          </v-col>

          <!-- ACCESSIBILITY DIALOG -->
          <v-col
            cols="12"
            v-if="
              showAccessibilityDialog &&
              !showClosingDialog &&
              !showEndConv &&
              !showWebrtcStream.dialog
            "
            style="position: relative"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <accessibility-options
              :show="showAccessibilityDialog"
              :fromChat="true"
              @close="closeAccessibilityModal"
            />
          </v-col>

          <!-- WEBCAM / CALL CONTAINER -->
          <v-col
            cols="12"
            v-if="
              (isInCall && $vuetify.breakpoint.smAndDown) ||
              (!isOnVideoCall && !$vuetify.breakpoint.smAndDown)
            "
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
            v-show="!showWebrtcStream.visible"
          >
            <call
              :show="isInCall"
              :video="isOnVideoCall"
              @hangupEv="hangup"
              @hideTextAreaEv="switchShowTextInput"
              @phone="onPhone"
              ref="callComponent"
            />
          </v-col>

          <!-- STREAM CONTAINER -->
          <v-col
            cols="12"
            v-if="showWebrtcStream.visible"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <v-row no-gutters style="position: relative">
              <v-btn
                @click="screenShareController.closeStream()"
                :class="{ oncall: isInCall }"
                icon
                id="closeStream"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>

              <video
                autoplay
                id="webrtcstream"
                :class="{ oncall: isInCall }"
                :src-object.prop.camel="showWebrtcStream.stream"
                width="300px"
                height="300px"
              ></video>
            </v-row>
          </v-col>

          <!-- CHAT CONTAINER -->
          <v-col
            ref="chatContainer"
            cols="12"
            class="overflow-x-hidden"
            :style="[
              {
                height: evaluateHeight,
                zoom: `1.${getWebchat().client.accessibility.fontSize}`,
              },
            ]"
            :class="{ 'show-chat': $store.getters.isStartedInteraction }"
          >
            <div
              class="holderChat chat-background"
              :class="{
                'relative-pos': getWebchat().isLowRes,
                holderChatWithVideo: isOnVideoCall,
              }"
            >
              <ol @click="$refs.textarea.blur()" class="chat">
                <message
                  v-for="(item, index) in getWebchat().interaction.messages"
                  :visible="!(index == 0 && hasParameters())"
                  :message="item"
                  :key="index"
                  @menuMessage="menuMessageEvent"
                ></message>

                <message v-if="typingVisible"></message>
              </ol>
            </div>
          </v-col>

          <!-- TEXTAREA / ATTACHMENTS CONTAINER -->
          <v-col
            cols="12"
            :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
          >
            <div
              class="containerButtons"
              v-show="
                showTextInput &&
                !this.endConv &&
                !getWebchat().interaction.transfering
              "
            >
              <textarea
                ref="textarea"
                @keyup.enter="sendMessage"
                v-model="typingText"
                class="textarea"
                type="text"
                :placeholder="$t('typehere')"
              ></textarea>

              <div v-if="$store.getters.hasTextareaText" class="btnTextarea">
                <v-btn icon @click="sendMessage">
                  <v-icon color="grey darken-1">mdi-send</v-icon>
                </v-btn>
              </div>

              <div
                v-else-if="getWebchat().campaign.allow_attachments"
                class="btnTextarea"
              >
                <v-btn v-if="this.attachUploading" icon disabled>
                  <v-icon class="rotate">mdi-autorenew</v-icon>
                </v-btn>
                <v-btn v-else icon @click.native="addAttachment">
                  <v-icon color="grey darken-1">mdi-paperclip</v-icon>
                </v-btn>
                <input
                  type="file"
                  v-on:change="onFileChange"
                  ref="inputfiles"
                  style="display: none"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- WAITING FOR INTERACTION FEEDBACK -->
    <v-card
      v-if="$store.getters.isWaitingInteraction"
      class="d-flex align-center"
      id="holderWaiting"
      :style="{ zoom: `1.${getWebchat().client.accessibility.fontSize}` }"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <div class="finishInteractionButton">
            <v-btn v-on="on" @click="showModalAbandon = true" icon>
              <v-icon
                :style="{
                  color: invertColor(getWebchat().campaign.color),
                }"
                >mdi-close</v-icon
              >
            </v-btn>
          </div>
        </template>
        <span>{{ $t("close") }}</span>
      </v-tooltip>
      <v-dialog v-model="showModalAbandon">
        <v-card>
          <v-card-title class="headline">{{ $t("closeconf") }}</v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green darken-1" text @click="clickModalAbandon()">{{
              $t("yes")
            }}</v-btn>
            <v-btn
              color="green darken-1"
              text
              @click="showModalAbandon = false"
              >{{ $t("no") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div class="holdTimeText">
        <p>
          <v-icon class="rotate">mdi-autorenew</v-icon>
        </p>
        <p v-html="getText"></p>
        <p>
          <chronometer></chronometer>
        </p>
      </div>
    </v-card>
  </transition>
</template>

<script>
import Constants from "../assets/Constants";
import { getUrlWebSocket, unifiedInboxNotification } from "@/assets/utils";
import { mapGetters } from "vuex";
import Message from "./message";
import Call from "./call.vue";
import Attachments from "@/assets/Attachments";
import DetectRTC from "detectrtc";
import Chronometer from "./chronometer";
import ChatToolbar from "./chat-toolbar";
import ClosingDialog from "./closing-dialog";
import AccessibilityOptions from "./accessibility-options";
import ScreenShareDialog from "./screen-share-dialog";
import ScreenShareController from "./../assets/ScreenShareController";
import { invertColor } from "@/assets/utils";
import ReconnectingWebSocket from "reconnecting-websocket";

export default {
  name: "chat",

  components: {
    Message,
    Call,
    Chronometer,
    ChatToolbar,
    ClosingDialog,
    ScreenShareDialog,
    AccessibilityOptions,
  },

  data() {
    return {
      showModalAbandon: false,
      interval: null,
      websocket: null,
      usertyping: false,
      windowcall: null,
      attachments: new Attachments(this.$http),
      transfering: false,
      isOnVideoCall: false,
      isInCall: false,
      showTextInput: true,
      showClosingDialog: false,
      lastOneMe: null,
      lastOneAgent: null,

      sentTyping: false,
      //showWebrtcStream will be only changed by ScreenShareContoller
      showWebrtcStream: {
        visible: false,
        dialog: false,
        stream: null,
      },
      showEndConv: false,
      endConv: false,
      bot: false,
      screenShareController: null,
      attachUploading: false,
      window: window,
      showAccessibilityDialog: false,
    };
  },

  computed: {
    // Store getters and setters
    typingText: {
      get() {
        const campaignName = this.$route.query.campaign;

        const index = this.$store.state.findIndex(
          (e) => e.configuration.campaign === campaignName
        );

        return this.$store.state[index].client.typingText;
      },

      set(value) {
        this.$store.commit("setTypingText", value);

        if (value && !this.sentTyping) {
          this.sendTyping();
        } else if (!value) {
          this.sendNotTyping();
        }
      },
    },

    typingVisible() {
      return this.usertyping;
    },

    notif: {
      get() {
        const campaignName = this.$route.query.campaign;

        const index = this.$store.state.findIndex(
          (e) => e.configuration.campaign === campaignName
        );

        return this.$store.state[index].notifications.notifications;
      },
      set(value) {
        this.$store.commit("setNotifications", value);
      },
    },

    getText() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let campaign = this.$store.state[index].campaign;
      let lang = this.$store.state[index].client.lang;

      return campaign.holdtimemessage[lang]
        ? campaign.holdtimemessage[lang]
        : campaign.holdtimemessage["en"];
    },

    ...mapGetters({
      imageAgent: "imageAgent",
    }),

    evaluateHeight() {
      let height = `calc(100vh / 1.${
        this.getWebchat().client.accessibility.fontSize
      } - 124px)`;

      if (this.$vuetify.breakpoint.smAndDown) {
        if (this.isInCall) {
          if (this.isOnVideoCall) {
            if (
              this.showClosingDialog ||
              this.showWebrtcStream.dialog ||
              this.showAccessibilityDialog
            ) {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 499px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 437px)`);
            } else {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 424px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 362px)`);
            }
          } else {
            if (
              this.showClosingDialog ||
              this.showWebrtcStream.dialog ||
              this.showAccessibilityDialog
            ) {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 499px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 249px)`);
            } else {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 424px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 174px)`);
            }
          }
        } else {
          if (
            this.showClosingDialog ||
            this.showWebrtcStream.dialog ||
            this.showAccessibilityDialog
          ) {
            return this.showWebrtcStream.visible
              ? (height = `calc(100vh / 1.${
                  this.getWebchat().client.accessibility.fontSize
                } - 499px)`)
              : (height = `calc(100vh / 1.${
                  this.getWebchat().client.accessibility.fontSize
                } - 199px)`);
          } else {
            return this.showWebrtcStream.visible
              ? (height = `calc(100vh / 1.${
                  this.getWebchat().client.accessibility.fontSize
                } - 424px)`)
              : height;
          }
        }
      } else {
        if (this.isInCall) {
          if (this.isOnVideoCall) {
            if (
              this.showClosingDialog ||
              this.showWebrtcStream.dialog ||
              this.showAccessibilityDialog
            ) {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 499px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 199px)`);
            } else {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 424px)`)
                : height;
            }
          } else {
            if (
              this.showClosingDialog ||
              this.showWebrtcStream.dialog ||
              this.showAccessibilityDialog
            ) {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 499px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 249px)`);
            } else {
              return this.showWebrtcStream.visible
                ? (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 424px)`)
                : (height = `calc(100vh / 1.${
                    this.getWebchat().client.accessibility.fontSize
                  } - 174px)`);
            }
          }
        } else {
          if (
            this.showClosingDialog ||
            this.showWebrtcStream.dialog ||
            this.showAccessibilityDialog
          ) {
            return this.showWebrtcStream.visible
              ? (height = `calc(100vh / 1.${
                  this.getWebchat().client.accessibility.fontSize
                } - 499px)`)
              : (height = `calc(100vh / 1.${
                  this.getWebchat().client.accessibility.fontSize
                } - 199px)`);
          } else {
            return this.showWebrtcStream.visible
              ? (height = `calc(100vh / 1.${
                  this.getWebchat().client.accessibility.fontSize
                } - 424px)`)
              : height;
          }
        }
      }
    },
  },

  created() {
    const campaignName = this.$route.query.campaign;

    const index = this.$store.state.findIndex(
      (e) => e.configuration.campaign === campaignName
    );

    if (this.$store.state[index].interaction.guid) {
      this.checkStateOnServer();
    } else {
      this.connectWebSocket();
    }

    //suscribe to events manager
    this.$eventsManager.susbscribeChat(this);
    this.$eventManagerMenu.subscribeChat(this);

    this.screenShareController = new ScreenShareController(
      this.sendWebSocket,
      this.showWebrtcStream,
      this.$store.state[index].isMobile
    );
  },

  mounted() {
    this.scrollBottom();
  },

  methods: {
    onPhone(phone) {
      phone.chat = this;
    },
    invertColor,

    hasParameters() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      return (
        this.$store.state[index].configuration.name &&
        this.$store.state[index].configuration.mail &&
        this.$store.state[index].configuration.initialMessage
      );
    },

    getWebchat() {
      return this.$store.state[
        this.$store.state.findIndex(
          (e) => e.configuration.campaign === this.$route.query.campaign
        )
      ];
    },

    menuMessageEvent(obj) {
      this.lastMenuMessage = obj;
    },

    disableLastMenuMessage(message) {
      if (this.lastMenuMessage) {
        this.lastMenuMessage.disableMenuMessage(message);
      }
    },
    /**
     * @name CHECK_SERVER_STATE
     * @desc Checks if current saved guid is still an active interaction
     */
    async checkStateOnServer() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      try {
        let response = await this.$http.get(
          "https://" +
            this.$store.state[index].configuration.ip +
            "/IntegraChannels/resources/webchat/thisGuid",
          {
            params: {
              guid: this.$store.state[index].interaction.guid,
            },
          }
        );

        if (!response.body[0]) {
          this.$router.push({
            path: "form",
            query: {
              campaign: campaignName,
            },
          });
        } else {
          this.connectWebSocket();
        }
      } catch (error) {
        this.$router.push({
          path: "form",
          query: {
            campaign: campaignName,
          },
        });
      }
    },

    /**
     * @name CONNECT_WEB_SOCKET
     * @desc defining websocket events
     */
    connectWebSocket() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let ip = this.$store.state[index].configuration.ip;
      let useruuid = this.$store.state[index].client.guid;
      this.websocket = new ReconnectingWebSocket(getUrlWebSocket(ip, useruuid));
      this.websocket.timeoutInterval = 5000;
      this.websocket.maxReconnectInterval = 5000;
      this.websocket.reconnectDecay = 1.1;
      this.websocket.onopen = this.onOpenWebSocket;
      this.websocket.onmessage = this.onMessageWebSocket;
      this.websocket.onclose = this.onCloseWebSocket;
      this.interval = setInterval(() => {
        //ping
        this.sendWebSocket({});
      }, 30000);
    },

    /**
     * @name ON_OPEN_WEBSOCKET
     * @desc on open we start the chat
     */
    onOpenWebSocket(event) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      if (
        this.$store.state[index].interaction.state ===
        Constants.NOT_STARTED_STATE
      ) {
        this.startChat();
      } else {
        window.parent.postMessage({ action: "CURRENT_URL" }, "*");
      }
    },

    /**
     * @name ON_CLOSE_WEBSOCKET
     * @desc no yet need to stablish behaviour
     */
    onCloseWebSocket(event) {
      console.log("websocket disconnected");
    },

    /**
     * @name ON_MESSAGE_WEBSOCKET
     * @desc On message we check event and define behaviour
     */
    onMessageWebSocket(event) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = JSON.parse(event.data);
      switch (msj.subtype) {
        case Constants.WS_CHAT_CREATED:
          // Interaction created, showing loading state
          this.chatCreated(msj);
          break;
        case Constants.WS_CONNECTED:
          // Interaction was connected with agent, showing chat with new agent
          this.connected(msj);
          break;
        case Constants.WS_NEW_MESSAGE:
          this.bot = msj.bot;
          this.newMessage(msj);

          // Checking if there's a custom message and show it the first time
          if (!this.$store.state[index].firstMessageDisplayed) {
            if (this.$store.state[index].campaign.first_message_agent) {
              let message = new Object();
              message.content =
                this.$store.state[index].campaign.first_message_agent;
              message.source = this.$store.state[index].interaction.telAgent;
              this.newMessage(message);
              this.$store.commit("changeFirstMessageDisplayed");
            }
          }
          break;
        case Constants.WS_CHAT_FINISHED:
          this.$store.commit("interactionFinished");
          this.finishChat(msj);
          break;
        case Constants.WS_TYPING:
          this.typing(msj);
          break;
        case Constants.WS_TIMEOUT:
          this.timeOut();
          break;
        case Constants.WS_TRANSFERRED:
          // Chat is being transferred, showing 'loading' state
          this.bot = false;
          this.$store.commit("startTransfering");
          break;
        case Constants.WS_ATTENDED:
          // Chat was transferred, showing chat with new agent
          this.connected(msj);
          this.$store.commit("endTransfering");
          break;
        case Constants.WS_REQUEST_SCREEN:
          this.screenShareController.onMessage(msj);
          break;
        case Constants.WS_SIGNALLING:
          this.screenShareController.onMessage(msj);
          break;
        case Constants.WS_WEBCHAT_EVENT:
          this.webChatEvent(msj.content);
          break;
      }
    },

    webChatEvent(content) {
      window.parent.postMessage(
        { action: "WEBCHAT_EVENT", data: content },
        "*"
      );
    },

    /**
     * @name SEND_WEBSOCKET_MSG
     * @desc Sends a pre-made ws message
     */
    sendWebSocket(data) {
      if (this.websocket) {
        this.websocket.send(JSON.stringify(data));
      }
    },

    /**
     * @description Sets remote description from peer offer and sends
     *              local description
     * @param {any} msg
     */

    sendTyping() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = {
        type: "webchat",
        subtype: "typing",
        content: "typing",
        source: this.$store.state[index].client.guid,
        guid: this.$store.state[index].interaction.guid,
      };
      this.sentTyping = true;
      this.sendWebSocket(msj);
    },

    sendStartCallEvent(video) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = {
        type: "webchat",
        subtype: "startcall",
        content: video ? "video" : "",
        source: this.$store.state[index].client.guid,
        guid: this.$store.state[index].interaction.guid,
      };
      this.sendWebSocket(msj);
    },

    sendEndCallEvent(video) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = {
        type: "webchat",
        subtype: "ENDCALL",
        content: video ? "video" : "",
        source: this.$store.state[index].client.guid,
        guid: this.$store.state[index].interaction.guid,
      };
      this.sendWebSocket(msj);
    },

    sendNotTyping() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = {
        type: "webchat",
        subtype: "typing",
        content: "nottyping",
        source: this.$store.state[index].client.guid,
        guid: this.$store.state[index].interaction.guid,
      };
      this.sentTyping = false;
      this.sendWebSocket(msj);
    },

    /**
     * @name START_CHAT
     * @desc Starts a new WC interaction sending a message through ws
     */
    startChat() {
      this.$store.commit("clearInteraction");

      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      // Creating interaction message
      let msj = {};
      msj.type = "webchat";
      msj.subtype = "startinteraction";
      msj.source = this.$store.state[index].client.guid;
      msj.content = {};
      msj.content.campaign = this.$store.state[index].configuration.campaign;
      msj.content.name = this.$store.state[index].client.name;
      msj.content.mail = this.$store.state[index].client.mail;
      msj.content.initialMessage =
        this.$store.state[index].client.initialMessage;
      msj.content.number = this.$store.state[index].client.number;
      msj.content.ipAddress = this.$store.state[index].client.ipAddress;
      msj.content.data = this.$store.state[index].client.data;

      this.sendWebSocket(msj);
    },

    /**
     * @name CHAT_CREATED
     * @desc Starts 'waiting interaction' loader
     */
    chatCreated(msj) {
      this.$store.commit("waitingInteraction");
      let guid = msj.guid;
      let holdTime = parseInt(msj.content);
      this.$store.commit("changeGuidInteraction", guid);
      // this.$store.commit("changeHoldTime", holdTime);
    },

    /**
     * @name NEW_MESSAGE
     * @desc Saves new message to messages_array
     */
    newMessage(msj) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      this.disableLastMenuMessage(true);
      let message = {};
      message.message = msj.content;

      message.agent = msj.source;
      message.attachments = msj.attachments;
      message.self = msj.source === this.$store.state[index].client.guid;
      message.lastOne = true;
      message.bot = msj.bot;
      this.$store.commit("newMessage", message);
      this.scrollBottom();
      this.$emit("addOneUnreadedMessagesCounter");
      window.parent.postMessage(
        { action: "NEWMESSAGE", message: message },
        "*"
      );
      !this.$store.state[index].open
        ? this.$store.commit("addOneUnreadedMessagesCounter")
        : "";

      if (msj.source !== this.$store.state[index].client.guid) {
        unifiedInboxNotification(
          this.$t("newmessage"),
          msj.content,
          () => {
            this.$refs.textarea.focus();
          },
          this.$store.state[index].menu.menu_img
        );
      }
    },

    /**
     * @name NEW_NOTIF_MESSAGE
     * @desc Saves new message to messages_array
     */
    newNotifMessage(msj) {
      this.disableLastMenuMessage(true);
      let message = {};
      message.message = msj;
      message.notif = true;

      this.$store.commit("newMessage", message);
      this.scrollBottom();
    },

    /**
     * @name FINISH_CHAT
     * @desc On finished interaction we re-route depending on conf
     */
    finishChat(msj) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      if (this.$store.state[index].interaction.state !== "finished") {
        window.parent.postMessage(
          {
            action: "COMPLETEAGENT",
            state: this.$store.state,
          },
          "*"
        );
      }

      if (this.interval) clearInterval(this.interval);
      if (this.websocket) this.websocket.close();
      this.endConv = true;
      this.openEndConvModal();
    },

    /**
     * @name ON_TYPING
     * @desc On typing, setting 'typing' state
     */
    typing(msj) {
      if (msj.content === "typing") {
        this.usertyping = true;
        this.scrollBottom();
      } else if (msj.content === "nottyping") {
        this.usertyping = false;
      }
    },

    /**
     * @name CONNECTED_INTERACTION
     * @desc On connected, saving interaction info
     */
    connected(msj) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      this.$store.commit("connectedInteraction");
      if (msj.content) {
        const nombreAgente = msj.content.agent || "Chat";
        const fullname = msj.content.fullname || nombreAgente;
        const telAgente = msj.content.agentnumber;

        if (nombreAgente) this.$store.commit("nameImage", nombreAgente);
        else if (this.state[index].campaign.logo == 1)
          this.$store.commit(
            "nameImage",
            this.state[index].configuration.campaign
          );
        if (fullname) this.$store.commit("nameAgent", fullname);

        this.$store.commit("telAgent", telAgente);
        this.$store.commit("username", msj.content.agent);

        if (msj.content.autoanswer == 1) this.openWindowCalling();
        else if (msj.content.autoanswer == 2) {
          this.openWindowVideocalling();
        }
      }
      window.parent.postMessage({ action: "CURRENT_URL" }, "*");
    },

    /**
     * @name SEND_MESSAGE
     * @desc Creates and sends a WC message through WS
     */
    sendMessage(e) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let text = this.$store.state[index].client.typingText
        .replaceAll(/<[^>]*>/g, "")
        .trim();
      if (text !== "") {
        let msj = {};
        msj.type = "webchat";
        msj.subtype = "newmessage";
        msj.source = this.$store.state[index].client.guid;
        msj.guid = this.$store.state[index].interaction.guid;
        msj.content = text;
        this.disableLastMenuMessage(msj.content);
        this.$store.commit("clearMessage");
        this.sendNotTyping();
        this.sendWebSocket(msj);
      } else {
        this.$store.commit("clearMessage");
      }
    },
    /**
     * @name SEND_CURRENT_USER_URL
     * @desc Sends the current user's URL
     */
    sendCurrentURL(obj) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = {};
      msj.type = "webchat";
      msj.subtype = "currentURL";
      msj.source = this.$store.state[index].client.guid;
      msj.guid = this.$store.state[index].interaction.guid;
      msj.content = obj;
      this.sendWebSocket(msj);
    },

    /**
     * @name ON_TIMEOUT
     * @desc On timeout, re routes to main page
     */
    timeOut() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      if (this.$store.state[index].interaction.transfering)
        this.$store.commit("endTransfering");
      this.$store.commit("setOffline");
      this.$router.push({
        path: "/form",
        query: {
          campaign: campaignName,
        },
      });
    },

    /**
     * @name FINISH_WC
     * @desc Sends message to terminate WC interaction
     */
    sendFinish() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      window.parent.postMessage(
        {
          action: "COMPLETECALLER",
          state: this.$store.state[index],
        },
        "*"
      );

      this.$store.commit("finishedInteraction");

      let msj = new Object();
      msj.type = "webchat";
      msj.subtype = "finishchat";
      msj.source = this.$store.state[index].client.guid;
      msj.guid = this.$store.state[index].interaction.guid;
      this.sendWebSocket(msj);
    },

    /**
     * @name ABANDON_WC
     * @desc Sends message to terminate WC interaction
     */
    sendAbandon() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      window.parent.postMessage(
        {
          action: "ABANDON",
          state: this.$store.state[index],
        },
        "*"
      );

      let msj = new Object();
      msj.type = "webchat";
      msj.subtype = "finishchat";
      msj.source = this.$store.state[index].client.guid;
      msj.guid = this.$store.state[index].interaction.guid;
      this.sendWebSocket(msj);
    },

    /**
     * @name CALLING
     * @desc Sets calling state to true
     */
    openWindowCalling() {
      this.isInCall = true;
      this.$store.commit("setIsInCall", true);
    },

    /**
     * @name VIDEO_CALLING
     * @desc Sets calling && video state to true
     */
    openWindowVideocalling() {
      this.isInCall = true;
      this.isOnVideoCall = true;
      this.$store.commit("setIsInCall", true);
    },

    openAccessibilityModal() {
      this.showAccessibilityDialog = !this.showAccessibilityDialog;
    },
    closeAccessibilityModal() {
      this.showAccessibilityDialog = false;
    },

    /**
     * @name HANG_UP
     * @desc Sets video and call to false
     */
    hangup() {
      this.isInCall = false;
      this.isOnVideoCall = false;
      this.scrollBottom();
    },

    switchShowTextInput() {
      this.showTextInput = !this.showTextInput;
    },

    addAttachment() {
      this.$refs.inputfiles.click();
    },

    scrollBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          let elem = this.$refs.chatContainer;
          elem.scrollTop = elem.scrollHeight;
        }
      });
    },

    focusTextArea() {
      this.$refs.textarea.focus();
    },

    openCloseDialog() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      if (this.$store.state[index].interaction.state == "finished") {
        this.showClosingDialog = false;
        this.showEndConv = true;
      } else {
        this.showClosingDialog = true;
        this.showEndConv = false;
      }

      this.showWebrtcStream.dialog = false;
    },

    hideClosingModal() {
      this.showClosingDialog = false;
    },

    clickModalAbandon() {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      this.showModalAbandon = false;
      this.sendAbandon();
      if (this.$store.state[index].campaign.allow_feedback_client) {
        this.$store.commit("finishedInteraction");
        this.$router.push({
          path: "/survey",
          query: {
            campaign: campaignName,
          },
        });
      } else {
        this.$store.commit("noStartedInteraction");
        this.$router.push({
          path: "/form",
          query: {
            campaign: campaignName,
          },
        });
      }
    },

    shouldClose(confirmation) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      if (confirmation) {
        if (this.isInCall) {
          this.$refs.callComponent.hangup();
        }
        if (this.endConv) {
          if (this.$store.state[index].campaign.allow_feedback_client) {
            this.$store.commit("finishedInteraction");
            this.$router.push({
              path: "/survey",
              query: {
                campaign: campaignName,
              },
            });
          } else {
            this.$store.commit("noStartedInteraction");
            this.$router.push({
              path: "/form",
              query: {
                campaign: campaignName,
              },
            });
          }
        }
        this.sendFinish();
      }
      this.showClosingDialog = false;
    },

    onFileChange(e) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      this.attachUploading = true;

      let result = this.attachments.addAttachment(e.target.files[0], (obj) => {
        this.disableLastMenuMessage(true);
        let msj = new Object();
        msj.type = "webchat";
        msj.subtype = "newmessage";
        msj.source = this.$store.state[index].client.guid;
        msj.guid = this.$store.state[index].interaction.guid;
        msj.content = "";
        msj.attachments = [obj];
        this.$store.commit("clearMessage");
        this.sendWebSocket(msj);
        this.attachUploading = false;
      });

      if (typeof result == "string") {
        this.newNotifMessage(this.$t(result));
        this.attachUploading = false;
      }
    },

    currentUrl(obj) {
      this.sendCurrentURL(obj);
    },

    sendOnlyText(text) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      let msj = {
        type: "webchat",
        subtype: "newmessage",
        source: this.$store.state[index].client.guid,
        guid: this.$store.state[index].interaction.guid,
        content: JSON.stringify(text),
        destination: "agent",
      };
      this.sendWebSocket(msj);
    },
    cancelModalEndConv() {
      this.showEndConv = false;
    },
    acceptModalEndConv(confirmation) {
      const campaignName = this.$route.query.campaign;

      const index = this.$store.state.findIndex(
        (e) => e.configuration.campaign === campaignName
      );

      if (confirmation) {
        window.parent.postMessage(
          {
            action: "FINISH_CHAT",
            state: this.$store.state[index],
          },
          "*"
        );
        if (this.$store.state[index].campaign.allow_feedback_client) {
          this.$store.commit("finishedInteraction");
          this.$router.push({
            path: "/survey",
            query: {
              campaign: campaignName,
            },
          });
        } else {
          this.$store.commit("noStartedInteraction");
          this.$router.push({
            path: "/form",
            query: {
              campaign: campaignName,
            },
          });
        }
      } else {
        this.showTextInput = false;
        this.showEndConv = false;
      }
    },
    openEndConvModal() {
      return (this.showEndConv = true), (this.showWebrtcStream.dialog = false);
    },
  },
};
</script>

<style>
@media screen and (max-width: 768px) {
  /* .chatContainer {
    left: 17px !important;
    width: calc(100% - 17px) !important;
  } */
}

/* Pages Transitions */
.slide-chat-enter-active {
  transition: all 0.3s ease;
  opacity: 1;
}

.slide-chat-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  position: absolute;
}

.slide-chat-enter,
.slide-chat-leave-to {
  transform: translateX(50px);
  opacity: 0;
}

#holderWaiting {
  height: 396px;
  margin: 26px;
  margin-top: -68px;
  z-index: 90;
  width: calc(100% - 50px);
  transition: all 0.3s ease-out;
}

.holdTimeText {
  width: 100%;
}

.holderChat {
  /* height: calc(100% - 104px); */
}

.chatContainer {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
}

html,
body {
  background: rgba(255, 255, 255, 0);
  margin: 0px auto;
}

::selection {
  background: rgba(82, 179, 217, 0.3);
  color: inherit;
}

.accionIconos {
  position: relative;
  border-radius: 6px 6px 0 0;
  top: 0px;
  height: 48px;
  font-size: 23px;
  width: 100%;
  color: #414141;
  border-bottom: 1px solid rgba(102, 102, 102, 0.29);
}

.accionIconos button {
  color: #777;
  cursor: pointer;
}

.chat {
  /* list-style: none;
  background: none;
  margin: 0;
  height: 100%;
  overflow-y: auto;
  width: 100%; */
}

.chat-background {
  background-repeat: repeat;
  background-size: 100%;
}

.chatWithVideo {
  margin-top: 262px !important;
  padding-top: 0px !important;
}

.holderChatWithVideo {
  /* height: calc(100% - 367px) !important; */
}

.containerButtons {
  bottom: 0;
  left: 0;
  right: 0;
  position: relative;
  border-top: 1px solid #e6e6e6;
  box-shadow: 0px -20px 50px 0px #0000000f;
}

.containerButtons-calling {
  height: 6em;
}

#containerTextArea {
  float: left;
  height: 100%;
  width: calc(100% - 40px);
}

.btnTextarea {
  z-index: 999999;
  height: 75px;
  width: 40px;
  text-align: center;
  height: 100%;
  float: left;
}

.btnTextarea button {
  position: absolute !important;
  top: 6px;
  right: 6px;
}

textarea.textarea {
  height: 100%;
  width: 100%;
  z-index: 99;
  border: none;
  outline: none;
  color: #666;
  font-weight: 400;
  padding-top: 8px;
  padding-right: 69px;
  padding-left: 10px;
  font-size: 14px;

  resize: none;
}

.containerSpinner {
  position: absolute;
  width: 50px;
  margin: auto;
  top: calc(31%);
  left: calc(50% - 15px);
}

.viewContainer {
  /* width: 100%;
  height: calc(100% - 212px); */
  /* position: absolute; */
  /* height: 100%;
  right: 0px;
  width: 100%;
  margin-top: -128px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12); */
}

.holdTimeText p {
  text-align: center;
  font-size: 16px;
  color: rgb(120, 120, 120);
}

.transfer-wc {
  position: absolute;
  top: 52px;
  left: 0;
  width: 100%;
  text-align: center;
}

.transfer-wc label {
  font-size: 16px;
  color: rgb(120, 120, 120);
}

/* Animated CSS */
.animated {
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}

@-webkit-keyframes fadeOutDown {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    -webkit-transform: translate3d(0, 100%, 0);
    transform: translate3d(0, 100%, 0);
  }
}

@keyframes fadeOutDown {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    -webkit-transform: translate3d(0, 100%, 0);
    transform: translate3d(0, 100%, 0);
  }
}

.fadeOutDown {
  -webkit-animation-name: fadeOutDown;
  animation-name: fadeOutDown;
}

/*END Animated CSS */

.external-window-chat-styles {
  border-radius: 0;
}

.application--wrap {
  min-height: 100% !important;
}

.rotate {
  animation: rotating 2s linear infinite;
}
@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style scoped>
.on-video-call {
  max-height: 45%;
}

.on-audio-call {
  height: 75%;
}

.mobile-accionIconos {
  display: flex;
  top: 0;
  width: 100%;
}

.mobile-wcall {
  top: 56px;
}

.width-100 {
  width: 100%;
}

.external-window-calling {
  height: 90% !important;
}

.external-window-videocalling {
  max-height: 45%;
}

.calling-chat-height {
  height: 100%;
}

.chatHolder {
  /* position: absolute; */
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  opacity: 0;
  transition: all 0.3s ease-out;
}

.chatHolder.show-chat {
  opacity: 1 !important;
  transition: all 0.3s ease-out;
}

.crop-view {
  position: relative;
  top: -71px;
}

.relative-pos {
  position: relative;
}

#webrtcstream {
  width: 100%;
  background-color: black;
  /* position: absolute; */
  /* z-index: 9999999; */
  /* top: 72px; */
}

#webrtcstream.oncall {
  /* top: 114px; */
}

#closeStream {
  position: absolute;
  right: 3px;
  top: 3px;
  color: #fff !important;
  z-index: 99999999;
  background-color: #ffffff63;
}

#closeStream.oncall {
  /* top: 110px; */
}
.finishInteractionButton {
  position: fixed;
  top: 10px;
  right: 10px;
}
</style>
